<template>
  <div class="log-viewer">
    <div class="log-header">
      <h3>Execution Logs</h3>
      <div class="log-filters">
        <label>
          <input type="checkbox" v-model="filters.info" @change="filterLogs" />
          Info
        </label>
        <label>
          <input type="checkbox" v-model="filters.warn" @change="filterLogs" />
          Warn
        </label>
        <label>
          <input type="checkbox" v-model="filters.error" @change="filterLogs" />
          Error
        </label>
        <label>
          <input type="checkbox" v-model="filters.debug" @change="filterLogs" />
          Debug
        </label>
      </div>
    </div>
    
    <div class="log-entries" ref="logContainer">
      <div 
        v-for="(log, index) in filteredLogs" 
        :key="index"
        class="log-entry"
        :class="`log-level-${log.level}`"
      >
        <span class="log-timestamp">{{ formatTimestamp(log.timestamp) }}</span>
        <span class="log-level" :style="{ color: getLevelStyle(log.level).color }">
          [{{ getLevelStyle(log.level).label }}]
        </span>
        <span v-if="log.nodename" class="log-node">{{ log.nodename }}</span>
        <span class="log-message">{{ log.message }}</span>
        <pre v-if="log.data" class="log-data">{{ formatData(log.data) }}</pre>
      </div>
      
      <div v-if="filteredLogs.length === 0" class="no-logs">
        No logs to display
      </div>
    </div>
  </div>
</template>

<script>
import { formatTimestamp, getLogLevelStyle } from '@/shared/workflowHistoryUtils';

export default {
  name: 'LogViewer',
  props: {
    logs: {
      type: Array,
      required: true,
      default: () => []
    }
  },
  data() {
    return {
      filters: {
        info: true,
        warn: true,
        error: true,
        debug: true
      },
      filteredLogs: []
    };
  },
  mounted() {
    this.filterLogs();
  },
  watch: {
    logs: {
      handler() {
        this.filterLogs();
      },
      deep: true
    }
  },
  methods: {
    formatTimestamp,
    getLevelStyle: getLogLevelStyle,
    filterLogs() {
      this.filteredLogs = this.logs.filter(log => {
        return this.filters[log.level] !== false;
      });
    },
    formatData(data) {
      if (!data) return '';
      return JSON.stringify(data, null, 2);
    }
  }
};
</script>

<style scoped>
.log-viewer {
  border: 1px solid var(--border-default);
  border-radius: 8px;
  background: var(--bg-card);
  overflow: hidden;
}

.log-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background-color: var(--form-bg);
  border-bottom: 1px solid var(--border-default);
}

.log-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
}

.log-filters {
  display: flex;
  gap: 16px;
}

.log-filters label {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: #9ca3af;
  cursor: pointer;
}

.log-filters input[type="checkbox"] {
  cursor: pointer;
}

.log-entries {
  max-height: 500px;
  overflow-y: auto;
  padding: 12px;
  background-color: var(--bg-main-end);
  color: #f9fafb;
  font-family: 'Monaco', 'Menlo', 'Courier New', monospace;
  font-size: 13px;
}

.log-entry {
  margin-bottom: 8px;
  padding: 6px 8px;
  border-radius: 4px;
  line-height: 1.5;
}

.log-entry:hover {
  background-color: var(--border-separator);
}

.log-timestamp {
  color: #9ca3af;
  margin-right: 8px;
}

.log-level {
  font-weight: 600;
  margin-right: 8px;
}

.log-node {
  color: var(--primary-start);
  margin-right: 8px;
  font-weight: 500;
}

.log-message {
  color: #f9fafb;
}

.log-data {
  margin: 4px 0 0 0;
  padding: 8px;
  background-color: rgba(0, 0, 0, 0.3);
  border-radius: 4px;
  color: #d1d5db;
  overflow-x: auto;
  font-size: 12px;
  border: 1px solid var(--border-default);
}

.no-logs {
  text-align: center;
  padding: 40px;
  color: #9ca3af;
  font-style: italic;
}
</style>
